import { useState } from "react";

import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { formatQty } from "@buildpy/core/lib/format";
import Money from "@/components/money";
import { cn } from "@/lib/utils";
import {
  statementQtyText,
  statementSummaryRows,
  statementTagOf,
  type CurrentStatement,
  type StatementAudience,
  type StatementLine,
} from "@buildpy/core/modules/change-order/current-statement";
import { deltaToneClass } from "@buildpy/core/modules/change-order/view";

interface Props {
  statement: CurrentStatement;
  audience?: StatementAudience;
  // 머리 카드(공사명·계약번호·차수별 증감·현재 계약금액) — 고객 화면은 자체 요약 카드가 있어 넘기지 않는다.
  head?: { title: string; contractNo: string };
}

const signedQty = (qty: number): string => `${qty > 0 ? "+" : qty < 0 ? "-" : ""}${formatQty(Math.abs(qty))}`;

const isChangedLine = (line: Pick<StatementLine, "deltas">): boolean => line.deltas.some((d) => d !== null);

const LineCard = ({ line, statement, tag, audience }: { line: StatementLine; statement: CurrentStatement; tag: string; audience: StatementAudience }) => {
  const changed = isChangedLine(line);
  return (
    <article className="flex flex-col gap-y-2 px-4 py-3" data-statement-line={changed ? "changed" : "same"}>
      <div className="flex items-start justify-between gap-x-3">
        <div className="min-w-0">
          <p className="break-words font-semibold">{line.label}</p>
          {tag && <p className="break-words text-xs text-muted-foreground">{tag}</p>}
        </div>
        <span className="shrink-0 text-xs text-muted-foreground">{line.unit}</span>
      </div>
      {changed ? (
        <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-baseline gap-x-2.5 gap-y-1 text-sm">
          <span className="text-xs text-muted-foreground">원 계약</span>
          <span className="text-xs tabular-nums text-muted-foreground">{formatQty(line.original_qty)}</span>
          <Money className="text-right" value={line.original_amount} />
          {line.deltas.map((d, i) =>
            d === null ? null : (
              <div key={statement.columns[i].id} className={cn("contents", deltaToneClass(d.amount))}>
                <span className="text-xs" title={statement.columns[i].title}>
                  {statement.columns[i].seq}차
                </span>
                <span className="text-xs tabular-nums">{signedQty(d.qty)}</span>
                <Money className="text-right" signed value={d.amount} />
              </div>
            )
          )}
          <span className="font-semibold">현재</span>
          <span className="text-xs tabular-nums text-muted-foreground">{statementQtyText(line, audience)}</span>
          <Money className="text-right font-semibold" value={line.current_amount} />
        </div>
      ) : (
        <div className="flex items-baseline justify-between gap-x-3 text-sm">
          <span className="text-xs tabular-nums text-muted-foreground">
            {statement.columns.length > 0 && "변경 없음 · "}
            {formatQty(line.original_qty)}
          </span>
          <Money className="font-semibold" value={line.current_amount} />
        </div>
      )}
    </article>
  );
};

// 현재 내역서 폰 카드 — md 이상·인쇄는 CurrentStatementTable 표를 쓰고, 폰은 공종 머리 아래 항목 카드로 세로로 쌓는다.
const CurrentStatementCards = ({ statement, audience = "staff", head }: Props) => {
  const [onlyChanged, setOnlyChanged] = useState(false);
  const t = statement.totals;
  const changedCount = statement.groups.reduce((n, g) => n + g.lines.filter(isChangedLine).length, 0);
  const groups = statement.groups
    .map((g) => ({ group: g, lines: onlyChanged ? g.lines.filter(isChangedLine) : g.lines }))
    .filter((g) => g.lines.length > 0);

  return (
    <div className="flex flex-col gap-y-3 md:hidden print:hidden" data-testid="current-statement-cards">
      {head && (
        <SectionCard className="text-sm" data-testid="current-statement-head">
          <p className="break-words text-xs text-muted-foreground">
            {head.title} · {head.contractNo}
          </p>
          <dl className="mt-2">
            <div className="flex justify-between gap-x-3 py-1.5">
              <dt className="text-muted-foreground">원 계약</dt>
              <dd className="whitespace-nowrap tabular-nums"><Money value={t.total.original} /></dd>
            </div>
            {statement.columns.map((c) => (
              <div key={c.id} className="flex justify-between gap-x-3 py-1.5">
                <dt className="min-w-0 break-words text-muted-foreground">
                  {c.seq}차 변경 · {c.title}
                </dt>
                <dd className={cn("whitespace-nowrap tabular-nums", deltaToneClass(c.delta_total))}><Money signed value={c.delta_total} /></dd>
              </div>
            ))}
            <div className="mt-1.5 flex justify-between gap-x-3 border-t border-border pt-2.5 font-semibold">
              <dt>현재 계약금액</dt>
              <dd className="whitespace-nowrap tabular-nums"><Money value={t.total.current} /></dd>
            </div>
          </dl>
          <p className="text-right text-xs tabular-nums text-muted-foreground">
            공급가 <Money value={t.current_supply} /> · 부가세 <Money value={t.current_vat} />
          </p>
        </SectionCard>
      )}

      {changedCount > 0 && (
        <div aria-label="변경 여부" className="flex flex-wrap gap-1.5" role="group">
          <Button aria-pressed={!onlyChanged} size="chip" type="button" variant="chip" onClick={() => setOnlyChanged(false)}>
            전체
          </Button>
          <Button aria-pressed={onlyChanged} size="chip" type="button" variant="chip" onClick={() => setOnlyChanged(true)}>
            변경된 것만 <span className="text-xs tabular-nums opacity-85">{changedCount}</span>
          </Button>
        </div>
      )}

      <SectionCard className="overflow-hidden text-sm" padding="none">
        {groups.map(({ group, lines }) => (
          <div key={group.key}>
            <div className="flex justify-between gap-x-3 bg-muted px-4 py-2.5 text-sm font-semibold text-muted-foreground">
              <span className="min-w-0 break-words">{group.label}</span>
              <Money value={group.current} />
            </div>
            <div className="divide-y divide-border">
              {lines.map((line) => (
                <LineCard key={line.key} audience={audience} line={line} statement={statement} tag={statementTagOf(line, statement.grouping, group.kind)} />
              ))}
            </div>
          </div>
        ))}
        <dl className="border-t border-border px-4 py-3">
          {statementSummaryRows(statement, audience).map((row) => (
            <div key={row.key} className={cn("flex justify-between gap-x-3 py-1", row.key === "total" && "font-semibold")}>
              <dt className="min-w-0">
                {row.label}
                {row.note && <span className="ml-1 text-xs font-normal text-muted-foreground">({row.note})</span>}
              </dt>
              <dd className="whitespace-nowrap tabular-nums"><Money value={row.current} /></dd>
            </div>
          ))}
        </dl>
      </SectionCard>
    </div>
  );
};

export default CurrentStatementCards;
